import useAddRequestStore from 'store/addRequestStore';
import AdditionalPreferences from './AdditionalPreferences';
import AddRequestHeader from './AddRequestHeader';
import BudgetTimeline from './BudgetTimeline';
import ContactInformation from './ContactInformation';
import EditInformation from './EditInformation';
import LocationDetails from './LocationDetails';
import PropertyDetails from './PropertyDetails';
import RequestOverview from './request-overview/RequestOverview';

export default function FinalPreview() {
    const { activeModal, setActiveModal, requestData, usePredefinedLocations } = useAddRequestStore();

    const modalMap: Record<string, React.ComponentType<{ isEditMode?: boolean }>> = {
        RequestOverview: RequestOverview,
        LocationDetails: LocationDetails,
        PropertyDetails: PropertyDetails,
        BudgetTimeline: BudgetTimeline,
        AdditionalPreferences: AdditionalPreferences,
        ContactInformation: ContactInformation,
    };

    return (
        <>
            <AddRequestHeader
                title="Review and Confirm Your Request"
                description="Take a moment to review your request details. If you need to make any changes, click the 'Edit' button in the relevant section."
            />

            <div className="space-y-5 mt-4">
                <Card title="Request Overview" onEdit={() => setActiveModal('RequestOverview')}>
                    <div className="space-y-4">
                        <KeyValuePair label="Request Title" value={requestData.title} />
                        <KeyValuePair label="Request Description" value={requestData.description} />
                    </div>
                </Card>

                <Card title="Location Details" onEdit={() => setActiveModal('LocationDetails')}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {usePredefinedLocations && requestData.definedArea ? (
                            <KeyValuePair label="Selected Area" value={requestData.definedArea} />
                        ) : (
                            <>
                                <KeyValuePair label="Country" value={requestData.propertyCountry} />
                                <KeyValuePair label="City" value={requestData.propertyCity.join(', ')} />
                                <KeyValuePair label="State" value={requestData.propertyState.join(', ')} />
                                {requestData.propertyNeighborhood && requestData.propertyNeighborhood.length > 0 && (
                                    <KeyValuePair label="Neighborhood" value={requestData.propertyNeighborhood.join(', ')} />
                                )}
                            </>
                        )}
                    </div>
                </Card>

                <Card title="Property Specifications" onEdit={() => setActiveModal('PropertyDetails')}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <KeyValuePair label="Property Type" value={requestData.propertyType} />
                        {requestData.minConstructionArea && requestData.maxConstructionArea && (
                            <KeyValuePair label="Constructed area" value={`${requestData.minConstructionArea}-${requestData.maxConstructionArea} m²`} />
                        )}
                        {requestData.minPlotArea && requestData.maxPlotArea && (
                            <KeyValuePair label="Plot area" value={`${requestData.minPlotArea}-${requestData.maxPlotArea} m²`} />
                        )}
                        <KeyValuePair label="Bedrooms" value={`${requestData.minBedrooms}-${requestData.maxBedrooms}`} />
                        <KeyValuePair label="Bathrooms" value={`${requestData.minBathrooms}-${requestData.maxBathrooms}`} />
                    </div>
                </Card>

                <Card title="Budget & Timeline" onEdit={() => setActiveModal('BudgetTimeline')}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <KeyValuePair label="Budget" value={`${requestData.minBudget}-${requestData.maxBudget} €`} />
                        <KeyValuePair label="Urgency" value={requestData.urgency as string} />
                        <KeyValuePair label="Agent Commission" value={`${requestData.commission}`} />
                        {requestData.buyerProfile && <KeyValuePair label="Buyer Profile" value={requestData.buyerProfile} />}
                    </div>
                </Card>

                <Card title="Additional Preferences" onEdit={() => setActiveModal('AdditionalPreferences')}>
                    <div className="grid grid-cols-1 gap-2">
                        {requestData.amenities && requestData.amenities.length > 0 && (
                            <KeyValuePair label="Amenities" value={(requestData.amenities as string[]).join(', ')} />
                        )}
                        {requestData.furnishing && <KeyValuePair label="Furnishing" value={requestData.furnishing} />}
                        {requestData.buyerProfile && <KeyValuePair label="Buyer Profile" value={requestData.buyerProfile} />}
                        {requestData.otherSpecificRequirements && (
                            <KeyValuePair label="Other Specific Requirements" value={requestData.otherSpecificRequirements} />
                        )}
                    </div>
                </Card>

                <Card title="Contact Information" onEdit={() => setActiveModal('ContactInformation')}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {requestData.contactInfo.name && <KeyValuePair label="Your Name" value={requestData.contactInfo.name} />}
                        {requestData.contactInfo.phone && <KeyValuePair label="Phone Number" value={requestData.contactInfo.phone} />}
                        {requestData.contactInfo.email && <KeyValuePair label="Email" value={requestData.contactInfo.email} />}
                        {requestData.contactInfo.website && <KeyValuePair label="Website" value={requestData.contactInfo.website} />}
                        {requestData.contactInfo.facebook && <KeyValuePair label="Facebook" value={requestData.contactInfo.facebook} />}
                        {requestData.contactInfo.linkedin && <KeyValuePair label="Linkedin" value={requestData.contactInfo.linkedin} />}
                        {requestData.contactInfo.instagram && <KeyValuePair label="Instagram" value={requestData.contactInfo.instagram} />}
                        {requestData.contactInfo.twitter && <KeyValuePair label="X" value={requestData.contactInfo.twitter} />}
                        {requestData.contactInfo.contactTime && <KeyValuePair label="Contact Time" value={requestData.contactInfo.contactTime} />}
                        {requestData.contactInfo.buyerIdentification && (
                            <KeyValuePair label="Buyer Identification" value={requestData.contactInfo.buyerIdentification} />
                        )}
                    </div>
                </Card>
            </div>
            {activeModal && <EditInformation visible={!!activeModal} onClose={() => setActiveModal('')} Component={modalMap[activeModal]} />}
        </>
    );
}

interface KeyValuePairProps {
    label: string;
    value: string;
}

export function KeyValuePair({ label, value }: KeyValuePairProps) {
    return (
        <div className="my-1">
            <dt className="text-base text-gray-500">{label}</dt>
            <dd className="mt-1.5 text-base ">{value}</dd>
        </div>
    );
}
interface CardProps {
    title: string;
    children: React.ReactNode;
    onEdit?: () => void;
}

export function Card({ title, children, onEdit }: CardProps) {
    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center bg-gray-50 justify-between p-3 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">{title}</h2>
                {onEdit && (
                    <button onClick={onEdit} className="text-base font-medium text-my-blue hover:text-blue-700 transition-colors">
                        Edit
                    </button>
                )}
            </div>
            <div className="p-6">{children}</div>
        </div>
    );
}
